import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { WebsiteUpsertRequest } from 'src/api/generated';
import { useCreateWebsitePreviewSession } from './api/websites';

/** Quiet time after the last edit before the preview asks for a session. */
export const WEBSITE_LIVE_PREVIEW_DEBOUNCE_MS = 1_500;

/**
 * Sessions remembered, so a draft already shown, such as a design tried
 * before, shows again without a new session.
 */
const MAX_REMEMBERED_SESSIONS = 8;

/**
 * Remaining lifetime below which a session is replaced instead of reused,
 * so a preview someone is browsing doesn't expire under them.
 */
const MIN_REMAINING_SESSION_MS = 10 * 60_000;

/** Longest delay a browser timer accepts. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/** A preview session as the preview needs it. */
interface PreviewSession {
  /** Preview URL of the session. */
  url: string;
  /** When the session stops answering, in epoch milliseconds. */
  expiresAt: number;
}

/** A created session that can show its draft again. */
interface RememberedSession extends PreviewSession {
  /** Draft the session shows, serialized for comparison. */
  signature: string;
}

/** The session on screen, kept while the next one for its design loads. */
interface ShownSession extends PreviewSession {
  /** Design of the draft the session shows. */
  templateId: string;
}

/** Inputs of the live preview. */
interface UseWebsiteLivePreviewParams {
  /** Draft currently in the editor. */
  form: WebsiteUpsertRequest;
  /** Whether the draft can be previewed: editable, complete, no logo work. */
  enabled: boolean;
  /** Whether a preview is on screen; sessions are created only then. */
  visible: boolean;
}

const getTemplateId = (form: WebsiteUpsertRequest): string =>
  [form.templateKey, form.templateVersion, form.registryTemplateId ?? ''].join(
    ':'
  );

const hasTimeToReuse = (session: PreviewSession): boolean =>
  session.expiresAt - Date.now() > MIN_REMAINING_SESSION_MS;

const findRememberedSession = (
  sessions: readonly RememberedSession[],
  signature: string
): RememberedSession | undefined =>
  sessions.find(
    (session) => session.signature === signature && hasTimeToReuse(session)
  );

/**
 * Keeps a preview session in step with the editor's draft, so the preview
 * shows exactly what publishing would: every text, the WhatsApp messages and
 * the agency's real listings.
 *
 * A session is created once the draft stops changing: at once for the draft
 * a preview opens on, and {@link WEBSITE_LIVE_PREVIEW_DEBOUNCE_MS} after the
 * last edit otherwise, and only while a preview is visible. Each draft gets
 * one request at a time: returning to a draft whose session is still being
 * created waits for it, a draft shown recently reuses its session, and a
 * session near its expiry is replaced. A draft whose session failed gets no
 * retry until it changes or another draft is shown in between.
 *
 * @param params - Draft, whether it can be previewed, and whether a preview
 * is visible.
 * @returns The session preview URL, or `undefined` while the caller should
 * show the mock preview instead: before the first session is ready, after a
 * session fails, after a design switch until its session is ready, once a
 * session has expired, and while the draft cannot be previewed.
 */
export const useWebsiteLivePreview = ({
  form,
  enabled,
  visible,
}: UseWebsiteLivePreviewParams): string | undefined => {
  const { createPreviewSession } = useCreateWebsitePreviewSession();
  const signature = useMemo(() => JSON.stringify(form), [form]);
  const settledSignature = useDebounce(
    signature,
    WEBSITE_LIVE_PREVIEW_DEBOUNCE_MS
  );
  const [rememberedSessions, setRememberedSessions] = useState<
    RememberedSession[]
  >([]);
  const [shownSession, setShownSession] = useState<ShownSession | null>(null);
  const [expiryChecks, setExpiryChecks] = useState(0);
  // Newest draft asked for; only its answer changes the shown session.
  const requestedSignatureRef = useRef<string | null>(null);
  // Drafts whose session is still being created.
  const pendingSignaturesRef = useRef(new Set<string>());
  const failedSignatureRef = useRef<string | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Looks again shortly before the shown session runs out, so a preview left
  // open gets a fresh session, and once more when it expires, so its URL is
  // withdrawn even while the fresh session is still on its way.
  useEffect(() => {
    if (!shownSession) {
      return undefined;
    }

    const timers = [
      shownSession.expiresAt - MIN_REMAINING_SESSION_MS,
      shownSession.expiresAt,
    ].map((checkAt) =>
      window.setTimeout(
        () => setExpiryChecks((count) => count + 1),
        Math.min(Math.max(checkAt - Date.now(), 0), MAX_TIMER_DELAY_MS)
      )
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [shownSession]);

  useEffect(() => {
    if (!enabled || !visible || settledSignature !== signature) {
      return;
    }

    const repeatsLatestRequest = requestedSignatureRef.current === signature;
    requestedSignatureRef.current = signature;
    const templateId = getTemplateId(form);
    const remembered = findRememberedSession(rememberedSessions, signature);
    if (remembered) {
      setShownSession((current) =>
        current?.url === remembered.url
          ? current
          : { templateId, url: remembered.url, expiresAt: remembered.expiresAt }
      );
      return;
    }

    const pendingSignatures = pendingSignaturesRef.current;
    if (
      pendingSignatures.has(signature) ||
      (repeatsLatestRequest && failedSignatureRef.current === signature)
    ) {
      return;
    }

    pendingSignatures.add(signature);
    const isLatestRequest = (): boolean =>
      mountedRef.current && requestedSignatureRef.current === signature;

    createPreviewSession(form).then(
      (session) => {
        pendingSignatures.delete(signature);
        if (!mountedRef.current) {
          return;
        }

        const created: RememberedSession = {
          signature,
          url: session.previewUrl,
          expiresAt: new Date(session.expiresAt).getTime(),
        };
        setRememberedSessions((current) =>
          [
            created,
            ...current.filter((item) => item.signature !== signature),
          ].slice(0, MAX_REMEMBERED_SESSIONS)
        );
        if (isLatestRequest()) {
          setShownSession({
            templateId,
            url: created.url,
            expiresAt: created.expiresAt,
          });
        }
      },
      () => {
        pendingSignatures.delete(signature);
        if (isLatestRequest()) {
          failedSignatureRef.current = signature;
          setShownSession(null);
        }
      }
    );
  }, [
    createPreviewSession,
    enabled,
    expiryChecks,
    form,
    rememberedSessions,
    settledSignature,
    signature,
    visible,
  ]);

  if (!enabled) {
    return undefined;
  }

  const remembered = findRememberedSession(rememberedSessions, signature);
  if (remembered) {
    return remembered.url;
  }

  return shownSession?.templateId === getTemplateId(form) &&
    shownSession.expiresAt > Date.now()
    ? shownSession.url
    : undefined;
};
